import { useAuth, Role } from '@shared/auth/AuthContext';
import { allSuites } from '@suites/registry';

export interface MobileTabItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  suite?: string;
  isMore?: boolean;
}

interface CandidateTab {
  id: string;
  label: string;
  icon: string;
  path: string;
  suite: string;
  permission?: string;
}

const ROLE_CANDIDATE_TABS: Record<Role, CandidateTab[]> = {
  owner: [
    { id: 'core', label: 'Core', icon: 'layout-dashboard', path: '/core/dashboard', suite: 'core' },
    { id: 'bi', label: 'Cockpit', icon: 'bar-chart', path: '/bi/executive', suite: 'bi', permission: 'bi.read' },
    { id: 'sis', label: 'Students', icon: 'graduation-cap', path: '/sis/students', suite: 'sis', permission: 'students.read' },
    { id: 'lms', label: 'Courses', icon: 'book-open', path: '/lms/courses', suite: 'lms', permission: 'courses.read' },
    { id: 'fees', label: 'Fees', icon: 'credit-card', path: '/fees/invoices', suite: 'fees', permission: 'fees.read' },
  ],
  admin: [
    { id: 'core', label: 'Core', icon: 'layout-dashboard', path: '/core/dashboard', suite: 'core' },
    { id: 'bi', label: 'Cockpit', icon: 'bar-chart', path: '/bi/executive', suite: 'bi', permission: 'bi.read' },
    { id: 'sis', label: 'Students', icon: 'graduation-cap', path: '/sis/students', suite: 'sis', permission: 'students.read' },
    { id: 'lms', label: 'Courses', icon: 'book-open', path: '/lms/courses', suite: 'lms', permission: 'courses.read' },
    { id: 'fees', label: 'Fees', icon: 'credit-card', path: '/fees/invoices', suite: 'fees', permission: 'fees.read' },
  ],
  branch_head: [
    { id: 'core', label: 'Core', icon: 'layout-dashboard', path: '/core/dashboard', suite: 'core' },
    { id: 'bi', label: 'Cockpit', icon: 'bar-chart', path: '/bi/executive', suite: 'bi', permission: 'bi.read' },
    { id: 'sis', label: 'Students', icon: 'graduation-cap', path: '/sis/students', suite: 'sis', permission: 'students.read' },
    { id: 'lms', label: 'Courses', icon: 'book-open', path: '/lms/courses', suite: 'lms', permission: 'courses.read' },
    { id: 'fees', label: 'Fees', icon: 'credit-card', path: '/fees/invoices', suite: 'fees', permission: 'fees.read' },
  ],
  teacher: [
    { id: 'core', label: 'Core', icon: 'layout-dashboard', path: '/core/dashboard', suite: 'core' },
    { id: 'live', label: 'Live', icon: 'video', path: '/lms/live', suite: 'lms', permission: 'live.join' },
    { id: 'schedule', label: 'Schedule', icon: 'calendar', path: '/scheduling/timetable', suite: 'scheduling', permission: 'schedule.read' },
    { id: 'courses', label: 'Courses', icon: 'book-open', path: '/lms/courses', suite: 'lms', permission: 'courses.read' },
  ],
  student: [
    { id: 'core', label: 'Home', icon: 'layout-dashboard', path: '/core/dashboard', suite: 'core' },
    { id: 'live', label: 'Live', icon: 'video', path: '/lms/live', suite: 'lms', permission: 'live.join' },
    { id: 'learn', label: 'Courses', icon: 'book-open', path: '/lms/learn', suite: 'lms', permission: 'courses.read' },
    { id: 'schedule', label: 'Schedule', icon: 'calendar', path: '/scheduling/timetable', suite: 'scheduling', permission: 'schedule.read' },
  ],
  parent: [
    { id: 'core', label: 'Home', icon: 'layout-dashboard', path: '/core/dashboard', suite: 'core' },
    { id: 'children', label: 'Children', icon: 'graduation-cap', path: '/sis/students', suite: 'sis', permission: 'students.read' },
    { id: 'fees', label: 'Fees', icon: 'credit-card', path: '/fees/invoices', suite: 'fees', permission: 'fees.read' },
    { id: 'schedule', label: 'Schedule', icon: 'calendar', path: '/scheduling/timetable', suite: 'scheduling', permission: 'schedule.read' },
  ],
  counselor: [
    { id: 'core', label: 'Core', icon: 'layout-dashboard', path: '/core/dashboard', suite: 'core' },
    { id: 'leads', label: 'Leads', icon: 'users', path: '/crm/leads', suite: 'crm', permission: 'crm.read' },
    { id: 'admissions', label: 'Admissions', icon: 'file-check', path: '/crm/admissions', suite: 'crm', permission: 'crm.read' },
    { id: 'students', label: 'Students', icon: 'graduation-cap', path: '/sis/students', suite: 'sis', permission: 'students.read' },
  ],
  accountant: [
    { id: 'core', label: 'Core', icon: 'layout-dashboard', path: '/core/dashboard', suite: 'core' },
    { id: 'invoices', label: 'Invoices', icon: 'receipt', path: '/fees/invoices', suite: 'fees', permission: 'fees.read' },
    { id: 'payments', label: 'Payments', icon: 'dollar-sign', path: '/fees/payments', suite: 'fees', permission: 'fees.read' },
    { id: 'ledger', label: 'Ledger', icon: 'calculator', path: '/fees/ledger', suite: 'fees', permission: 'fees.read' },
  ],
};

export const MORE_TAB: MobileTabItem = {
  id: 'more',
  label: 'More',
  icon: 'layout-grid',
  path: '#more',
  isMore: true,
};

export function useMobileTabs(): MobileTabItem[] {
  const { role, enabledSuites, hasPermission } = useAuth();

  const candidates = ROLE_CANDIDATE_TABS[role] || ROLE_CANDIDATE_TABS.admin;

  const validTabs: MobileTabItem[] = candidates
    .filter((cand) => {
      // Check if suite is enabled (if enabledSuites is specified)
      if (enabledSuites && enabledSuites.length > 0 && !enabledSuites.includes(cand.suite)) {
        return false;
      }
      // Check permissions
      if (cand.permission && !hasPermission(cand.permission)) {
        return false;
      }
      return true;
    })
    .slice(0, 4)
    .map((c) => ({
      id: c.id,
      label: c.label,
      icon: c.icon,
      path: c.path,
      suite: c.suite,
    }));

  // If fewer than 4 tabs due to filtered suites, backfill with available suites
  if (validTabs.length < 4) {
    for (const suite of allSuites) {
      if (validTabs.length >= 4) break;
      if (suite.id === 'platform') continue;
      if (enabledSuites && enabledSuites.length > 0 && !enabledSuites.includes(suite.id)) continue;
      if (suite.requiredPermission && !hasPermission(suite.requiredPermission)) continue;
      if (validTabs.some((t) => t.suite === suite.id)) continue;

      validTabs.push({
        id: suite.id,
        label: suite.name,
        icon: suite.icon,
        path: suite.basePath,
        suite: suite.id,
      });
    }
  }

  return [...validTabs, MORE_TAB];
}
